import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { labTestOrderAPI } from '../services/api';
import { STATUS_COLORS, minsUntilViewable } from '../utils/labTestUtils';
import Icon from '../components/Icon';
import OverflowMenu from '../components/OverflowMenu';
import { displayPatientCode } from '../utils/patientCode';

const BREADCRUMBS = [{ label: 'Lab Tests' }];

// Phone layout (max-width 768px) — see the "Lab Tests list — phone layout"
// block at the end of index.css. Everything tagged lab-mlist-* is restyled
// there, and the mlist-phone-only / mlist-desktop-only pieces show on one side
// of the breakpoint only; the desktop table is untouched.
const sentenceCase = (s = '') => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase().replace(/_/g, ' ');
const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '—';
const PAGE_SIZE = 20;
const createdLabel = (t) => (t.createdAt
  ? new Date(t.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '');

export default function LabTestListPage() {
  const navigate = useNavigate();
  const [tests,       setTests]       = useState([]);
  const [total,       setTotal]       = useState(0);
  const [stats,       setStats]       = useState({ total: 0, ordered: 0, completed: 0, createdToday: 0 });
  const [loading,     setLoading]     = useState(true);
  const [listError,   setListError]   = useState('');
  const [search,      setSearch]      = useState('');
  const [statusFilter, setStatus]     = useState('ALL');
  const [page,        setPage]        = useState(0);
  const [, setTick] = useState(0); // drives countdown re-renders
  const [syncingId,   setSyncingId]   = useState(null);
  const [syncError,   setSyncError]   = useState('');

  // Re-render every 30s so countdown labels stay accurate while the page is open
  useEffect(() => {
    const id = setInterval(() => setTick(n => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const fetchTests = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const res   = await labTestOrderAPI.list({
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        search: search.trim() || undefined,
        page,
        size:   PAGE_SIZE,
      });
      const items = res.data?.items || [];
      setTests(items);
      setTotal(res.data?.total ?? 0);
      const s = res.data?.stats;
      if (s) {
        setStats({
          total:        s.total        ?? 0,
          ordered:      s.ordered      ?? 0,
          completed:    s.completed    ?? 0,
          createdToday: s.createdToday ?? 0,
        });
      } else {
        setStats({
          total:        items.length,
          ordered:      items.filter(t => t.status === 'ORDERED').length,
          completed:    items.filter(t => t.status === 'COMPLETED').length,
          createdToday: items.filter(t => {
            if (!t.createdAt) return false;
            return new Date(t.createdAt).toDateString() === new Date().toDateString();
          }).length,
        });
      }
    } catch (err) {
      setTests([]);
      setTotal(0);
      setStats({ total: 0, ordered: 0, completed: 0, createdToday: 0 });
      setListError(
        err.response?.data?.message
          || err.message
          || 'Could not load lab test orders.',
      );
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page]);

  useEffect(() => {
    const timer = setTimeout(() => { fetchTests(); }, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [fetchTests, search]);

  const filtered = tests;
  const shownTotal = total || tests.length;
  const hasPrev = page > 0;
  const hasNext = (page + 1) * PAGE_SIZE < shownTotal;
  // A new search or filter always starts again from the first page.
  const changeSearch = (v) => { setSearch(v); setPage(0); };
  const changeStatus = (v) => { setStatus(v); setPage(0); };
  // The stats strip only has counts for these statuses.
  const chipCounts = { ALL: stats.total, ORDERED: stats.ordered, COMPLETED: stats.completed };

  // Retries Thyrocare order creation for a local order that has no partner
  // order — see useLabTestBooking.js's handleProceedToPayment: the local
  // order is saved even when the Thyrocare createOrder call fails, so the
  // navigator's wallet charge isn't lost, leaving partnerOrderId empty.
  const handleSyncToPartner = async (order) => {
    const orderKey = order.testCode || order.id;
    setSyncingId(orderKey);
    setSyncError('');
    try {
      await labTestOrderAPI.syncToPartner(orderKey);
      await fetchTests();
    } catch (err) {
      setSyncError(
        err.response?.data?.message
          || err.message
          || 'Could not sync this order to Thyrocare.',
      );
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <AppShell breadcrumbs={BREADCRUMBS}>
      <div className="page-header mlist-header fade-in-up">
        <div className="mlist-header-text">
          <div className="page-title">Lab tests</div>
          <div className="page-subtitle mlist-desktop-only">Track orders, collections and reports in one place.</div>
          {(!loading || stats.total > 0) && (
            <div className="mlist-count mlist-phone-only">{stats.total} lab test{stats.total !== 1 ? 's' : ''}</div>
          )}
        </div>
        <div className="page-actions">
          <button className="btn btn-primary mlist-primary" onClick={() => navigate('/lab-tests/new')}>
            <span className="mlist-desktop-only btn-with-icon"><Icon name="plus" size={16} /> New lab test</span>
            <span className="mlist-phone-only mlist-btn-label"><Icon name="plus" size={16} /> Create lab test</span>
          </button>
        </div>
      </div>

      {/* One bordered strip on phones (mlist-stats), four cards on desktop. */}
      <div className="stats-row mlist-stats fade-in-up delay-1">
        <div className="stat-card stat-card--icon">
          <div className="stat-icon stat-icon--green"><Icon name="vial" size={20} /></div>
          <div className="stat-body">
            <div className="stat-label">All lab tests</div>
            <div className="stat-value">{stats.total}</div>
            <div className="stat-change">all time</div>
          </div>
        </div>
        <div className="stat-card stat-card--icon">
          <div className="stat-icon stat-icon--amber"><Icon name="clock" size={20} /></div>
          <div className="stat-body">
            <div className="stat-label">Awaiting collection</div>
            <div className="stat-value">{stats.ordered}</div>
            <div className="stat-change">ordered, not yet collected</div>
          </div>
        </div>
        <div className="stat-card stat-card--icon">
          <div className="stat-icon stat-icon--green"><Icon name="check" size={20} /></div>
          <div className="stat-body">
            <div className="stat-label">Reports ready</div>
            <div className="stat-value">{stats.completed}</div>
            <div className="stat-change">results available</div>
          </div>
        </div>
        <div className="stat-card stat-card--icon">
          <div className="stat-icon stat-icon--blue"><Icon name="bolt" size={20} /></div>
          <div className="stat-body">
            <div className="stat-label">Created today</div>
            <div className="stat-value">{stats.createdToday}</div>
            <div className="stat-change">new orders</div>
          </div>
        </div>
      </div>

      {listError && (
        <div className="alert alert-error fade-in-up delay-1" style={{ marginBottom: 16 }}>
          <Icon name="alert" size={14} /> {listError}
        </div>
      )}

      {syncError && (
        <div className="alert alert-error fade-in-up delay-1" style={{ marginBottom: 16 }}>
          <Icon name="alert" size={14} /> {syncError}
        </div>
      )}

      <div className="toolbar mlist-toolbar fade-in-up delay-2">
        <div className="search-input-wrap">
          <label className="mlist-sr-only" htmlFor="lab-test-search">Search lab tests</label>
          <span className="search-icon"><Icon name="search" size={15} /></span>
          {/* The fields LabTestOrderSpecifications searches. */}
          <input
            id="lab-test-search"
            className="search-input"
            placeholder="Search by patient, plan, test code, order ID, patient code or phone…"
            value={search}
            onChange={e => changeSearch(e.target.value)}
          />
        </div>
        {/* display: contents on desktop, so the chips stay toolbar items
            there; on phones this is their own horizontally scrolling line. */}
        <div className="mlist-chips" role="group" aria-label="Filter by status">
          {['ALL', 'ORDERED', 'COLLECTED', 'COMPLETED', 'CANCELLED'].map(s => (
            <button
              key={s}
              className={`btn btn-sm mlist-chip ${statusFilter === s ? 'btn-primary' : 'btn-ghost'}`}
              aria-pressed={statusFilter === s}
              onClick={() => changeStatus(s)}
            >
              {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
              {chipCounts[s] != null && <span className="chip-count">{chipCounts[s]}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="table-wrap lab-mlist fade-in-up delay-3">
        <table>
          <thead>
            <tr>
              <th>Patient</th>
              <th>Plan</th>
              <th>Tests</th>
              <th>Created</th>
              <th>Price</th>
              <th>Status</th>
              <th><span className="mlist-sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7}>
                  <div className="loading-state">
                    <div className="spinner" />
                    Loading lab tests…
                  </div>
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="empty-state">
                    <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="vial" size={20} /></div>
                    <div className="empty-title">No lab tests found</div>
                    <div className="empty-desc">
                      {search || statusFilter !== 'ALL'
                        ? 'Try adjusting your search or filters'
                        : 'Click New lab test to order your first one'}
                    </div>
                  </div>
                </td>
              </tr>
            ) : filtered.map(t => {
              const orderId  = t.partnerOrderId || t.partner?.orderId;
              const plan     = t.planDisplayName || t.plan || t.planName;
              const metaTail = [orderId, createdLabel(t)].filter(Boolean).join(' · ');
              // partnerOrderSyncedAt is when Thyrocare actually learned about
              // this order — for a synced order (see handleSyncToPartner)
              // that's well after t.createdAt (the LOCAL order's original,
              // possibly much older, creation time). Gating the lock on
              // createdAt alone let a navigator click "View order" right
              // after a successful sync, before Thyrocare finished indexing
              // the order, and hit its own "accepted but still syncing" error.
              const minsLeft = orderId ? minsUntilViewable(t.partnerOrderSyncedAt || t.createdAt) : 0;
              const locked   = minsLeft > 0;
              const syncing  = syncingId === (t.testCode || t.id);
              // Phones: the row's "⋯" menu carries what the Actions column holds.
              const menuItems = [
                locked && { label: `Order opens in ~${minsLeft} min`, disabled: true, onSelect: () => {} },
                !orderId && { label: syncing ? 'Syncing…' : 'Sync to Thyrocare', disabled: syncing, onSelect: () => handleSyncToPartner(t) },
                t.patientId && { label: 'View patient', onSelect: () => navigate(`/patients/${t.patientId}`) },
              ].filter(Boolean);
              return (
              <tr key={t.id} className="lab-mlist-row">
                <td className="lab-mlist-c-main">
                  <div className="patient-cell">
                  <span className="avatar avatar--t2 mlist-desktop-only" aria-hidden="true">{initials(t.patientName)}</span>
                  <div className="patient-cell__text">
                  <div className="lab-mlist-name">{t.patientName || '—'}</div>
                  <div className="lab-mlist-sub">
                    {orderId && <span className="mono mlist-desktop-only" title="Thyrocare partner order ID">{orderId} · </span>}
                    {[displayPatientCode(t.patientCode), t.patientPhone].filter(Boolean).join(' · ')}
                  </div>
                  </div>
                  </div>
                  {/* The plan name is what clips, so the order ID and date stay readable. */}
                  <div className="lab-mlist-meta mlist-phone-only">
                    {plan && <span className="lab-mlist-meta-plan">{plan}</span>}
                    {metaTail && <span className="lab-mlist-meta-tail">{plan ? ` · ${metaTail}` : metaTail}</span>}
                  </div>
                  {/* Phones: stretched over the row, so the whole row opens the order. */}
                  {orderId && !locked && (
                    <Link className="lab-mlist-link mlist-phone-only" to={`/lab-tests/orders/${orderId}`}
                      data-testid="lab-order-row-link"
                      aria-label={`Open lab order for ${t.patientName || 'this patient'}`} />
                  )}
                </td>
                {/* DEF-005 / TC-N020 — "Raw Thyrocare SKUs visible in Navigator
                    catalog." planName is the lab partner's own product name,
                    frozen into the order row, so historical orders still read
                    "AAROGYAM 1.1". planDisplayName is the Previu-branded name
                    the server resolves from the panel tier — prefer it, and keep
                    planName only as a last resort so a row never renders blank. */}
                <td className="lab-mlist-c-plan">{plan || '—'}</td>
                <td className="lab-mlist-c-tests">{t.noOfTests ?? t.testsIncluded?.length ?? '—'}</td>
                <td className="lab-mlist-c-date">{createdLabel(t) || '—'}</td>
                <td className="lab-mlist-c-price">
                  {t.price != null
                    ? `${t.currency === 'INR' ? '₹' : (t.currency || '')}${Number(t.price).toLocaleString('en-IN')}`
                    : '—'}
                </td>
                <td className="lab-mlist-c-status">
                  <span className={`badge badge--dot ${STATUS_COLORS[t.status] || 'badge-pending'}`}>
                    <span className="mlist-desktop-only">{t.status || 'Ordered'}</span>
                    <span className="mlist-phone-only">{sentenceCase(t.status || 'Ordered')}</span>
                  </span>
                </td>
                <td className="lab-mlist-c-end">
                  <div className="mlist-desktop-only">
                    {orderId ? (
                      <button
                        type="button"
                        className="row-open"
                        onClick={() => navigate(`/lab-tests/orders/${orderId}`)}
                        disabled={locked}
                        aria-label={locked ? `Order opens in about ${minsLeft} minutes` : `Open lab order for ${t.patientName || 'this patient'}`}
                        title={locked ? `Thyrocare is processing this order. Try again in ~${minsLeft} min.` : 'View order'}
                      >
                        {locked ? <span style={{ fontSize: 11, fontWeight: 700 }}>~{minsLeft}m</span> : <Icon name="arrowRight" size={16} />}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleSyncToPartner(t)}
                        disabled={syncing}
                        title="No Thyrocare order was created for this booking yet — retry pushing it to Thyrocare"
                      >
                        {syncing ? 'Syncing…' : 'Sync to Thyrocare'}
                      </button>
                    )}
                  </div>
                  {menuItems.length > 0 && (
                    <OverflowMenu
                      className="mlist-phone-only"
                      label={`More actions for ${t.patientName || 'this order'}`}
                      items={menuItems}
                    />
                  )}
                  {t.patientId && (
                    <OverflowMenu
                      className="mlist-desktop-only"
                      label={`More actions for ${t.patientName || 'this order'}`}
                      items={[{ label: 'View patient', onSelect: () => navigate(`/patients/${t.patientId}`) }]}
                    />
                  )}
                  {orderId && !locked && (
                    <span className="mlist-chevron mlist-phone-only" aria-hidden="true"><Icon name="chevronRight" size={18} /></span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        <div className="table-footer">
          <span>
            {loading
              ? 'Loading…'
              : `Showing ${filtered.length} of ${shownTotal} order${shownTotal !== 1 ? 's' : ''}`}
          </span>
          {(hasPrev || hasNext) && (
            <div className="pager">
              <button type="button" className="btn btn-ghost btn-sm" disabled={!hasPrev || loading} onClick={() => setPage(p => p - 1)}>
                Previous
              </button>
              <button type="button" className="btn btn-secondary btn-sm btn-with-icon" disabled={!hasNext || loading} onClick={() => setPage(p => p + 1)}>
                Next <Icon name="arrowRight" size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
